import React, { useState } from 'react';
import './GlossaryTerm.css';

export function GlossaryTerm({ term, definition, children }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef(null);

  const handleClick = (e) => {
    if (!isExpanded && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.top - 8, // 8px above the button
        left: rect.left + rect.width / 2,
      });
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <button
      ref={buttonRef}
      className={`glossary-term ${isExpanded ? 'is-expanded' : ''}`}
      onClick={handleClick}
      title={isExpanded ? 'Click to hide definition' : 'Click to show definition'}
      type="button"
      aria-label={`${term}: ${definition}`}
    >
      <span className="glossary-term__text">{children}</span>
      {isExpanded && (
        <span
          className="glossary-term__tooltip"
          style={{
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <strong>{term}:</strong> {definition}
        </span>
      )}
    </button>
  );
}

export function parseGlossaryText(text, glossaryMap) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  const parts = [];
  let lastIndex = 0;
  const glossaryRegex = /\[\[([^\]]+)\]\]/g;
  let match;

  while ((match = glossaryRegex.exec(text)) !== null) {
    const termName = match[1];
    const entry = glossaryMap[termName];

    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the glossary term or plain text if not in glossary
    if (entry) {
      parts.push({
        type: 'glossaryTerm',
        term: entry.canonical || termName,
        definition: typeof entry === 'string' ? entry : entry.definition,
        key: `glossary-${lastIndex}`,
      });
    } else {
      parts.push(termName);
    }

    lastIndex = glossaryRegex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}

export function renderGlossaryText(text, glossaryMap) {
  const parts = parseGlossaryText(text, glossaryMap);

  if (typeof parts === 'string') {
    return parts;
  }

  return parts.map((part, idx) => {
    if (typeof part === 'string') {
      return part;
    }
    if (part.type === 'glossaryTerm') {
      return (
        <GlossaryTerm
          key={part.key || idx}
          term={part.term}
          definition={part.definition}
        >
          {part.term}
        </GlossaryTerm>
      );
    }
    return null;
  });
}
